import { t } from '@store/I18nStore'

/** Stored tone id — same for every event; files differ per event folder. */
export const NOTIFICATION_SOUND_IDS = [
  'sound-1',
  'sound-2',
  'sound-3',
  'sound-4',
] as const

export type SoundTone = (typeof NOTIFICATION_SOUND_IDS)[number]

export type SoundEventKey = 'new_conversation' | 'accepted_dialog' | 'dialog_transfer'

export type NotificationSoundOption = {
  id: SoundTone
  label: string
}

export function getNotificationSoundOptions(): NotificationSoundOption[] {
  return NOTIFICATION_SOUND_IDS.map((id, index) => ({
    id,
    label: t('profile.sounds.signal', { number: index + 1 }),
  }))
}

/** Files under `/public/assets/audio/<event>/` — index 0..3 = sound-1..sound-4. */
const EVENT_SOUND_SRCS: Record<SoundEventKey, readonly [string, string, string, string]> = {
  new_conversation: [
    '/assets/audio/new_conversation/ringtone-1.mp3',
    '/assets/audio/new_conversation/ringtone-2.mp3',
    '/assets/audio/new_conversation/ringtone-3.mp3',
    '/assets/audio/new_conversation/ringtone-4.mp3',
  ],
  accepted_dialog: [
    '/assets/audio/accepted_dialog/signal-1.mp3',
    '/assets/audio/accepted_dialog/signal-2.mp3',
    '/assets/audio/accepted_dialog/signal-3.mp3',
    '/assets/audio/accepted_dialog/signal-4.mp3',
  ],
  dialog_transfer: [
    '/assets/audio/dialog_transfer/signal-1.mp3',
    '/assets/audio/dialog_transfer/signal-2.mp3',
    '/assets/audio/dialog_transfer/signal-3.mp3',
    '/assets/audio/dialog_transfer/signal-4.mp3',
  ],
}

const LEGACY_SOUND_MAP: Record<string, SoundTone> = {
  default: 'sound-1',
  short: 'sound-2',
  soft: 'sound-3',
  'ringtone-1': 'sound-1',
  'ringtone-2': 'sound-2',
  'ringtone-3': 'sound-3',
  'ringtone-4': 'sound-4',
  'signal-1': 'sound-1',
  'signal-2': 'sound-2',
  'signal-3': 'sound-3',
  'signal-4': 'sound-4',
}

type PendingPlay = { eventKey: SoundEventKey; tone: SoundTone }

export function getNotificationSoundOption(tone: SoundTone): NotificationSoundOption {
  const options = getNotificationSoundOptions()
  return options.find((option) => option.id === normalizeSoundTone(tone)) ?? options[0]
}

let currentAudio: HTMLAudioElement | null = null
let audioCtx: AudioContext | null = null
let mediaUnlocked = false
let unlockListenersAttached = false
let unlockHandler: (() => void) | null = null
let pendingPlay: PendingPlay | null = null

export function isSoundTone(value: unknown): value is SoundTone {
  return typeof value === 'string' && (NOTIFICATION_SOUND_IDS as readonly string[]).includes(value)
}

export function normalizeSoundTone(value: unknown, fallback: SoundTone = 'sound-1'): SoundTone {
  const raw = String(value ?? '').trim().toLowerCase()
  if (isSoundTone(raw)) return raw
  if (raw in LEGACY_SOUND_MAP) return LEGACY_SOUND_MAP[raw]
  return fallback
}

export function getNotificationSoundSrc(eventKey: SoundEventKey, tone: SoundTone): string {
  const normalized = normalizeSoundTone(tone)
  const index = NOTIFICATION_SOUND_IDS.indexOf(normalized)
  const srcs = EVENT_SOUND_SRCS[eventKey]
  return srcs[index >= 0 ? index : 0] ?? srcs[0]
}

function getAudioContextConstructor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
    null
  )
}

function ensureAudioContext(): AudioContext | null {
  const Ctor = getAudioContextConstructor()
  if (!Ctor) return null
  if (!audioCtx || audioCtx.state === 'closed') {
    try {
      audioCtx = new Ctor()
    } catch {
      return null
    }
  }
  return audioCtx
}

/** Resume AudioContext only after a user gesture — never call from mount/effects. */
function kickAudioContextResume(): void {
  const ctx = ensureAudioContext()
  if (!ctx || ctx.state === 'running') return
  try {
    const result = ctx.resume()
    if (result && typeof result.then === 'function') {
      void result.then(() => {
        if (ctx.state === 'running') mediaUnlocked = true
      }).catch(() => {
        /* keep waiting for a real user gesture */
      })
    }
  } catch {
    /* ignore */
  }
}

function playBeepFallback(eventKey: SoundEventKey): boolean {
  const ctx = ensureAudioContext()
  if (!ctx || ctx.state !== 'running') return false

  const profile =
    eventKey === 'new_conversation'
      ? { frequency: 880, duration: 0.22, gain: 0.09 }
      : eventKey === 'dialog_transfer'
        ? { frequency: 520, duration: 0.28, gain: 0.08 }
        : { frequency: 660, duration: 0.15, gain: 0.08 }

  try {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.value = profile.frequency
    gain.gain.value = profile.gain
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + profile.duration)
    return true
  } catch {
    return false
  }
}

function stopCurrentAudio() {
  if (!currentAudio) return
  try {
    currentAudio.pause()
    currentAudio.currentTime = 0
  } catch {
    /* ignore */
  }
  currentAudio = null
}

function detachUnlockListeners() {
  if (!unlockHandler || typeof window === 'undefined') return
  window.removeEventListener('pointerdown', unlockHandler)
  window.removeEventListener('keydown', unlockHandler)
  unlockHandler = null
  unlockListenersAttached = false
}

function flushPendingPlay() {
  if (!pendingPlay) return
  const next = pendingPlay
  pendingPlay = null
  playNotificationSound(next.eventKey, next.tone)
}

/**
 * Attach gesture listeners until media is unlocked. Must stay attached after failed
 * unlock attempts so queued notification tones are not lost forever.
 */
function ensureAudioUnlockListeners() {
  if (typeof window === 'undefined' || unlockListenersAttached || mediaUnlocked) return
  unlockListenersAttached = true

  const unlock = () => {
    kickAudioContextResume()

    // Replay queued tone while we still have user activation (HTMLAudio.play works here).
    if (pendingPlay) {
      flushPendingPlay()
    } else {
      // Silent warm-up so later WS-triggered plays are allowed.
      try {
        const warm = new Audio(EVENT_SOUND_SRCS.new_conversation[0])
        warm.volume = 0
        const attempt = warm.play()
        if (attempt && typeof attempt.then === 'function') {
          void attempt
            .then(() => {
              warm.pause()
              warm.currentTime = 0
              mediaUnlocked = true
              detachUnlockListeners()
            })
            .catch(() => {
              /* stay listening */
            })
        } else {
          mediaUnlocked = true
          detachUnlockListeners()
        }
      } catch {
        /* stay listening */
      }
    }
  }

  unlockHandler = unlock
  window.addEventListener('pointerdown', unlock, { passive: true })
  window.addEventListener('keydown', unlock)
}

/** Arm unlock listeners early (after login). Do not resume AudioContext here — that requires a gesture. */
export function initNotificationAudioUnlock() {
  ensureAudioUnlockListeners()
}

/**
 * Play a notification tone. Never awaits AudioContext.resume() on the hot path —
 * that promise can hang without a user gesture and previously swallowed the queue.
 */
export function playNotificationSound(
  eventKey: SoundEventKey,
  tone: SoundTone = 'sound-1',
) {
  ensureAudioUnlockListeners()

  const normalized = normalizeSoundTone(tone)
  const src = getNotificationSoundSrc(eventKey, normalized)

  try {
    stopCurrentAudio()
    const audio = new Audio(src)
    audio.preload = 'auto'
    audio.volume = 1
    currentAudio = audio

    const playAttempt = audio.play()
    if (playAttempt && typeof playAttempt.then === 'function') {
      void playAttempt
        .then(() => {
          mediaUnlocked = true
          detachUnlockListeners()
        })
        .catch((err) => {
          console.warn('[notificationSounds] HTMLAudio blocked, queuing until gesture:', err)
          if (currentAudio === audio) currentAudio = null
          pendingPlay = { eventKey, tone: normalized }
          mediaUnlocked = false
          ensureAudioUnlockListeners()
          // If AudioContext is already running, fall back to a short beep now.
          if (playBeepFallback(eventKey)) {
            pendingPlay = null
            mediaUnlocked = true
            detachUnlockListeners()
          }
        })
    } else {
      mediaUnlocked = true
      detachUnlockListeners()
    }
  } catch (err) {
    console.warn('[notificationSounds] play failed:', err)
    pendingPlay = { eventKey, tone: normalized }
    mediaUnlocked = false
    ensureAudioUnlockListeners()
    if (playBeepFallback(eventKey)) {
      pendingPlay = null
      mediaUnlocked = true
      detachUnlockListeners()
    }
  }
}

export function previewNotificationSound(eventKey: SoundEventKey, tone: SoundTone) {
  pendingPlay = null
  kickAudioContextResume()
  mediaUnlocked = true
  playNotificationSound(eventKey, tone)
}
